import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Share2, Loader2, Facebook, Instagram, Music2, Wand2, Image as ImageIcon } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";

const API = getApiUrl();

export default function PropertyAdCreator({ propertyId, propertyData }) {
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [adText, setAdText] = useState("");
  const [propertyUrl, setPropertyUrl] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [socialMediaEnabled, setSocialMediaEnabled] = useState({
    facebook: false,
    instagram: false,
    tiktok: false
  });

  useEffect(() => {
    if (showDialog) {
      checkSocialMedia();
    }
  }, [showDialog]);

  const checkSocialMedia = async () => {
    try {
      const response = await axios.get(`${API}/property-admin/social-media`, {
        withCredentials: true
      });
      
      setSocialMediaEnabled({
        facebook: response.data.facebook_enabled || false,
        instagram: response.data.instagram_enabled || false,
        tiktok: response.data.tiktok_enabled || false
      });
    } catch (error) {
      console.error("Failed to check social media:", error);
    }
  };

  const handleGenerateAd = async () => {
    try {
      setGenerating(true);
      const response = await axios.post(
        `${API}/property-admin/property/${propertyId}/ad/generate`,
        {},
        { withCredentials: true }
      );
      
      setAdText(response.data.ad_text);
      setPropertyUrl(response.data.property_url || "");
      toast.success("Ad text generated successfully!");
    } catch (error) {
      console.error("Failed to generate ad:", error);
      toast.error("Failed to generate ad text");
    } finally {
      setGenerating(false);
    }
  };

  const handlePushAd = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error("Please select at least one platform");
      return;
    }

    if (!adText.trim()) {
      toast.error("Please generate or enter ad text");
      return;
    }

    try {
      setPushing(true);
      const response = await axios.post(
        `${API}/property-admin/property/${propertyId}/ad/push`,
        {
          ad_text: adText,
          platforms: selectedPlatforms
        },
        { withCredentials: true }
      );

      // Show results for each platform
      const results = response.data.results;
      let successCount = 0;
      let failCount = 0;

      Object.keys(results).forEach(platform => {
        const result = results[platform];
        const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
        
        if (result.success) {
          successCount++;
          const postId = result.post_id ? ` (Post ID: ${result.post_id})` : '';
          toast.success(`✓ Posted to ${platformName}${postId}`, { duration: 5000 });
        } else {
          failCount++;
          const errorDetails = result.error_code ? ` (Error: ${result.error_code})` : '';
          toast.error(`✗ ${platformName}: ${result.message}${errorDetails}`, { duration: 10000 });
        }
      });

      if (successCount > 0) {
        toast.success(`Ad pushed to ${successCount} platform(s)`);
        setShowDialog(false);
      }
    } catch (error) {
      console.error("Failed to push ad:", error);
      toast.error(error.response?.data?.detail || "Failed to push ad");
    } finally {
      setPushing(false);
    }
  };

  const togglePlatform = (platform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  const hasAnySocialMedia = socialMediaEnabled.facebook || socialMediaEnabled.instagram || socialMediaEnabled.tiktok;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setShowDialog(true);
          if (!adText) {
            handleGenerateAd();
          }
        }}
        className="gap-2"
      >
        <Share2 className="h-4 w-4" />
        Create Social Media Ad
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Create Social Media Ad</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {!hasAnySocialMedia && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="pt-6">
                  <p className="text-sm text-orange-800">
                    ⚠️ No social media accounts connected. Please configure your social media credentials in your profile settings before pushing ads.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Property Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Home Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  {propertyData.main_image && (
                    <div className="w-32 h-32 rounded overflow-hidden border flex-shrink-0">
                      <img
                        src={resolveImageUrl(propertyData.main_image)}
                        alt={propertyData.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{propertyData.title}</h3>
                    <p className="text-sm text-muted-foreground">{propertyData.display_address}</p>
                    <p className="text-lg font-bold text-primary mt-2">
                      {propertyData.currency}{propertyData.price?.toLocaleString()}
                    </p>
                    <div className="flex gap-4 text-sm text-muted-foreground mt-2">
                      {propertyData.beds && <span>🛏️ {propertyData.beds} bed</span>}
                      {propertyData.baths && <span>🚿 {propertyData.baths} bath</span>}
                    </div>
                  </div>
                </div>

                {/* Gallery preview — every photograph listed here will be pushed
                    to the social-media post (Facebook caps at 10). Skips video /
                    floorplan / EPC entries; keeps only media_type 1 (Photograph)
                    and 6 (Virtual Tour) items. */}
                {(() => {
                  const main = propertyData.main_image ? [propertyData.main_image] : [];
                  const gallery = Array.isArray(propertyData.gallery) ? propertyData.gallery : [];
                  const galleryUrls = gallery.reduce((acc, item) => {
                    if (item && typeof item === "object") {
                      const mt = Number(item.media_type) || 1;
                      if ((mt === 1 || mt === 6) && item.media_url) acc.push(item.media_url);
                    } else if (typeof item === "string" && item) {
                      acc.push(item);
                    }
                    return acc;
                  }, []);
                  const allImages = [...new Set([...main, ...galleryUrls])].slice(0, 10);
                  if (allImages.length === 0) return null;
                  return (
                    <div className="pt-3 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-medium">Images to publish ({allImages.length})</Label>
                        {allImages.length === 10 && (
                          <span className="text-xs text-amber-700">Capped at 10 (Facebook limit)</span>
                        )}
                      </div>
                      <div className="grid grid-cols-5 gap-2" data-testid="ad-gallery-preview">
                        {allImages.map((img, i) => (
                          <div
                            key={i}
                            className="aspect-square rounded overflow-hidden border bg-muted"
                            title={img}
                          >
                            <img
                              src={resolveImageUrl(img)}
                              alt={`property image ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        All these images will be attached to the Facebook post as a single multi-photo gallery.
                      </p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Ad Text */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="ad_text">Ad Text</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateAd}
                  disabled={generating}
                  className="gap-2"
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4" />
                  )}
                  {generating ? "Generating..." : "Regenerate"}
                </Button>
              </div>
              {propertyUrl && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <p className="text-blue-900 font-medium mb-1">Home Link:</p>
                  <a 
                    href={propertyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {propertyUrl}
                  </a>
                  <p className="text-xs text-blue-700 mt-1">This link is included in your ad text below</p>
                </div>
              )}
              <Textarea
                id="ad_text"
                value={adText}
                onChange={(e) => setAdText(e.target.value)}
                placeholder="Ad text will be generated automatically or you can write your own..."
                rows={10}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Edit the ad text to customize your message. The property URL is automatically included.
              </p>
            </div>

            {/* Platform Selection */}
            <div className="space-y-3">
              <Label>Select Platforms to Push</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50">
                  <Checkbox
                    id="facebook"
                    checked={selectedPlatforms.includes("facebook")}
                    onCheckedChange={() => togglePlatform("facebook")}
                    disabled={!socialMediaEnabled.facebook}
                  />
                  <Facebook className="h-5 w-5 text-blue-600" />
                  <Label htmlFor="facebook" className="flex-1 cursor-pointer">
                    Facebook
                    {!socialMediaEnabled.facebook && (
                      <span className="text-xs text-muted-foreground ml-2">(Not configured)</span>
                    )}
                  </Label>
                </div>

                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50">
                  <Checkbox
                    id="instagram"
                    checked={selectedPlatforms.includes("instagram")}
                    onCheckedChange={() => togglePlatform("instagram")}
                    disabled={!socialMediaEnabled.instagram}
                  />
                  <Instagram className="h-5 w-5 text-pink-600" />
                  <Label htmlFor="instagram" className="flex-1 cursor-pointer">
                    Instagram
                    {!socialMediaEnabled.instagram && (
                      <span className="text-xs text-muted-foreground ml-2">(Not configured)</span>
                    )}
                  </Label>
                </div>

                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50">
                  <Checkbox
                    id="tiktok"
                    checked={selectedPlatforms.includes("tiktok")}
                    onCheckedChange={() => togglePlatform("tiktok")}
                    disabled={!socialMediaEnabled.tiktok}
                  />
                  <Music2 className="h-5 w-5 text-black" />
                  <Label htmlFor="tiktok" className="flex-1 cursor-pointer">
                    TikTok
                    {!socialMediaEnabled.tiktok && (
                      <span className="text-xs text-muted-foreground ml-2">(Not configured)</span>
                    )}
                  </Label>
                </div>
              </div>
            </div>

            {/* Push Button */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handlePushAd}
                disabled={pushing || selectedPlatforms.length === 0 || !hasAnySocialMedia}
              >
                {pushing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {pushing ? "Pushing..." : "Push Ad to Social Media"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
